/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { FormSelect } from "../form/FormSelect";
import DropdownFilter from "../modals/DropdownFilter";
import { useDashboardStats } from "../../store/useDashboardStats";
import { getStoreColor } from "../../constants/plasticColors";

// Extend dayjs with weekOfYear plugin
dayjs.extend(weekOfYear);

interface Props {
  periodProp?: "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly";
  selectedFiltersProp?: string[];
  weeklyRangeProp?: Date;
  monthRangeProp?: Date;
  yearRangeProp?: Date;
  className?: string;
}

export const RevenuePerCustomerChart = React.memo(
  ({
    periodProp,
    selectedFiltersProp,
    weeklyRangeProp,
    monthRangeProp,
    yearRangeProp,
    className,
  }: Props) => {
    /** Store */
    const { revenuePerCustomerStats, getRevenuePerCustomerStats } =
      useDashboardStats();

    /** Refs */
    const snapshotRef = useRef(null);
    const [_, takeScreenshot] = useScreenshot();

    /** State */
    const [period, setPeriod] = useState<
      "Daily" | "Weekly" | "LastTwelweMonths" | "Monthly" | "Yearly"
    >("Monthly");
    const [yearRange, setYearRange] = useState(new Date());
    const [weeklyRange, setWeeklyRange] = useState(new Date());
    const [selectedLocations, setSelectedLocations] = useState<string[]>([
      "All Locations",
    ]);

    /** Process API Data */
    const processedData = useMemo(() => {
      if (!revenuePerCustomerStats || revenuePerCustomerStats.length === 0) {
        return {
          series: [],
          categories: [],
        };
      }

      // Extract unique timestamps for categories
      const allTimestamps = new Set<number>();
      revenuePerCustomerStats.forEach((location) => {
        location.data.forEach(([timestamp]) => {
          allTimestamps.add(timestamp);
        });
      });

      const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => a - b);

      // Convert timestamps to readable format based on period
      const categories = sortedTimestamps.map((timestamp) => {
        const date = new Date(timestamp);
        switch (period) {
          case "Yearly":
            return date.getFullYear().toString();
          case "Monthly":
            return date.toLocaleDateString("en-US", {
              month: "short",
              year: "numeric",
            });
          case "Weekly":
            return date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          default:
            return date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
        }
      });

      // Create series data
      const series = revenuePerCustomerStats.map((location) => {
        const dataMap = new Map<number, number>();
        location.data.forEach(([timestamp, value]) => {
          dataMap.set(timestamp, value);
        });

        const data = sortedTimestamps.map((timestamp) => {
          return dataMap.get(timestamp) || 0;
        });

        return {
          name: location.name,
          data,
        };
      });

      return {
        series,
        categories,
      };
    }, [revenuePerCustomerStats, period]);

    /** Effects */
    useEffect(() => {
      if (periodProp) setPeriod(periodProp);
      if (yearRangeProp) setYearRange(yearRangeProp);
      if (weeklyRangeProp) setWeeklyRange(weeklyRangeProp);
    }, [periodProp, yearRangeProp]);

    useEffect(() => {
      // Fetch data when component mounts or filters change
      const selectedStores = selectedLocations.includes("All Locations")
        ? undefined
        : selectedLocations.filter((loc) => loc !== "All Locations");

      let apiPeriod: "yearly" | "quarterly" | "monthly" | "weekly" = "yearly";
      switch (period) {
        case "Yearly":
          apiPeriod = "yearly";
          break;
        case "Monthly":
          apiPeriod = "monthly";
          break;
        case "Weekly":
          apiPeriod = "weekly";
          break;
        default:
          apiPeriod = "yearly";
      }

      if (apiPeriod === "weekly") {
        getRevenuePerCustomerStats(
          apiPeriod,
          undefined, // year
          undefined, // quarter
          undefined, // month
          weeklyRange, // week date
          weeklyRange, // requestedDay
          selectedStores
        );
      } else if (apiPeriod === "monthly") {
        getRevenuePerCustomerStats(
          apiPeriod,
          yearRange.getFullYear(),
          undefined,
          undefined,
          undefined,
          yearRange,
          selectedStores
        );
      } else if (apiPeriod === "yearly") {
        getRevenuePerCustomerStats(
          apiPeriod,
          yearRange.getFullYear(),
          undefined,
          undefined,
          undefined,
          yearRange,
          selectedStores
        );
      } else {
        getRevenuePerCustomerStats(
          apiPeriod,
          yearRange.getFullYear(),
          undefined,
          undefined,
          undefined,
          yearRange,
          selectedStores
        );
      }
    }, [
      period,
      yearRange,
      weeklyRange,
      selectedLocations,
      getRevenuePerCustomerStats,
    ]);

    /** Callbacks */
    const onPrev = () => {
      if (period === "Weekly") {
        setWeeklyRange(dayjs(weeklyRange).subtract(7, "day").toDate());
        return;
      }
      if (
        period === "Monthly" ||
        period === "Yearly" ||
        period === "LastTwelweMonths"
      ) {
        setYearRange(dayjs(yearRange).subtract(1, "year").toDate());
      }
    };
    const onNext = () => {
      if (period === "Weekly") {
        const nextWeek = dayjs(weeklyRange).add(7, "day");
        if (nextWeek.isAfter(dayjs())) return; // do not go beyond today
        setWeeklyRange(nextWeek.toDate());
        return;
      }
      if (
        period === "Monthly" ||
        period === "Yearly" ||
        period === "LastTwelweMonths"
      ) {
        const next = dayjs(yearRange).add(1, "year");
        if (next.isAfter(dayjs())) return;
        setYearRange(next.toDate());
      }
    };

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "revenue_per_customer.png";
        link.click();
      });
    };

    const filterOptions = useMemo(() => {
      if (!revenuePerCustomerStats || revenuePerCustomerStats.length === 0) {
        return ["All Locations"];
      }

      const locations = revenuePerCustomerStats.map((stat) => stat.name);
      return ["All Locations", ...locations];
    }, [revenuePerCustomerStats]);

    const onSelectFilters = useCallback(
      (option: string) => {
        if (option === "All Locations") {
          if (selectedLocations.includes("All Locations")) {
            setSelectedLocations([]);
            return;
          }
          setSelectedLocations(filterOptions);
          return;
        }
        setSelectedLocations((prev: string[]) =>
          prev.includes(option)
            ? prev
                .filter((item: string) => item !== option)
                ?.filter((item: string) => item !== "All Locations")
            : [...prev, option]?.filter(
                (item: string) => item !== "All Locations"
              )
        );
      },
      [selectedLocations]
    );

    const seriesColors = useMemo(
      () =>
        (processedData.series ?? []).map((series) =>
          getStoreColor(series?.name)
        ),
      [processedData.series]
    );

    /** Chart Configuration */
    const chartOptions = useMemo(
      () => ({
        chart: {
          type: "line" as const,
          height: 350,
          toolbar: {
            show: true,
          },
          zoom: {
            enabled: true,
          },
        },
        stroke: {
          curve: "smooth" as const,
          width: 2,
        },
        markers: {
          size: 4,
        },
        xaxis: {
          categories: processedData.categories,
          title: {
            text: "Month",
            style: {
              fontSize: "14px",
              fontWeight: 600,
              color: "#263238",
            },
          },
        },
        yaxis: {
          title: {
            text: "Revenue per Customer (€)",
            style: {
              fontSize: "14px",
              fontWeight: 600,
              color: "#263238",
            },
          },
          labels: {
            formatter: (value: number) => `€${value.toFixed(2)}`,
          },
        },
        colors:
          seriesColors.length > 0
            ? seriesColors
            : ["#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6"],
        legend: {
          position: "bottom" as const,
          horizontalAlign: "center" as const,
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
            // Filter series based on selected locations
            const filteredSeries = series
              .map((seriesValues: any, index: number) => ({
                name: w.globals.seriesNames[index],
                value: seriesValues[dataPointIndex],
                color: w.config.colors[index],
              }))
              .filter((item: any) => {
                // Show all if "All Locations" is selected
                if (selectedLocations.includes("All Locations")) {
                  return true;
                }
                // Show only selected locations
                return selectedLocations.includes(item.name);
              });

            // Sort by value in descending order (highest to lowest)
            filteredSeries.sort((a: any, b: any) => b.value - a.value);

            // Build the tooltip HTML with original ApexCharts styling
            let tooltipHtml =
              '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
            tooltipHtml += `${processedData.categories[dataPointIndex]}</div>`;

            filteredSeries.forEach((item: any) => {
              const formattedValue = `€${item.value.toFixed(2)}`;

              tooltipHtml += `
                 <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
                   <span class="apexcharts-tooltip-marker" style="background-color: ${
                     item.color
                   };"></span>
                   <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                     <div class="apexcharts-tooltip-y-group">
                       <span class="apexcharts-tooltip-text-y-label">${
                         item.name
                       }: </span>
                       <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
                     </div>
                   </div>
                 </div>
               `;
            });

            return tooltipHtml;
          },
        },
        grid: {
          borderColor: "#f1f1f1",
        },
      }),
      [processedData.categories, selectedLocations, seriesColors]
    );

    return (
      <div
        className={`flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10 ${className || ""}`}
      >
        <div className="flex flex-col sm:flex-row w-full items-center justify-between mb-12 gap-2">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
            Revenue per Customer by Location
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-2">
            <div className="flex items-center gap-2">
              <PrimaryButton size="sm" onClick={onPrev} label="Prev" />
              <div className="font-satoshi font-medium text-gray-text">
                {period === "Weekly"
                  ? `${dayjs(weeklyRange).startOf("week").format("DD MMM")} - ${dayjs(
                      weeklyRange
                    )
                      .endOf("week")
                      .format("DD MMM YYYY")}`
                  : yearRange.getFullYear()}
              </div>
              <PrimaryButton
                size="sm"
                disabled={
                  period === "Weekly"
                    ? dayjs(weeklyRange).add(7, "day").isAfter(dayjs())
                    : dayjs(yearRange).add(1, "year").isAfter(dayjs())
                }
                onClick={onNext}
                label="Next"
              />
            </div>

            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {period === "LastTwelweMonths" ? "Last 12 months" : period}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "Weekly"}
                  onClick={() => setPeriod("Weekly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  disabled={period === "Monthly"}
                  onClick={() => setPeriod("Monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "LastTwelweMonths"}
                  onClick={() => setPeriod("LastTwelweMonths")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Last 12 months
                </MenuItem>
                <MenuItem
                  disabled={period === "Yearly"}
                  onClick={() => setPeriod("Yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>

            <DropdownFilter
              label="Filters"
              options={filterOptions}
              selectedOptions={selectedLocations}
              onSelect={onSelectFilters}
            />

            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>

        <div ref={snapshotRef} className="w-full">
          <Chart
            options={chartOptions}
            series={processedData.series}
            type="line"
            height={350}
          />
        </div>
      </div>
    );
  }
);
